import { create } from 'zustand';
import type {
  ColumnMeta,
  ColumnOverrides,
  DatasetPreview,
  DatasetStatus,
} from '@/types/dataset';
import type { ColumnTransform } from '@/types/query';
import type { ColumnProfile } from '@/engine/profiler';
import { configDb } from '@/persistence/config-db';
import { useFiltersStore } from '@/store/filters-store';

const DASHBOARD_NAME_KEY_PREFIX = 'dashboardName:';

/** Strip the file extension off a filename to get a sensible default name. */
export function defaultDashboardName(fileName: string): string {
  return fileName.replace(/\.[^.]+$/, '') || fileName;
}

/** Resolve the effective name: user-set value, otherwise the file basename. */
export function effectiveDashboardName(entry: {
  dashboardName?: string;
  fileName: string;
}): string {
  const trimmed = entry.dashboardName?.trim();
  return trimmed && trimmed.length > 0
    ? trimmed
    : defaultDashboardName(entry.fileName);
}

/**
 * Per-dataset entry in the registry.
 */
export type DatasetEntry = {
  id: string;
  fileName: string;
  rowCount: number;
  columns: ColumnMeta[];
  preview: DatasetPreview;
  /** Result of `profileDataset` — populated asynchronously after upload. */
  profile?: ColumnProfile[];
  /** Manual column-type overrides applied on top of auto-detection. */
  overrides?: ColumnOverrides;
  /** Per-column value transforms (synonym maps + numeric bins). */
  valueGroupings?: Record<string, ColumnTransform>;
  /**
   * User-editable dashboard label. Defaults to the file basename on
   * upload (set in `addDataset`). Surfaces in the page header and
   * drives export filenames (Excel / JSON / PDF). Persisted via the
   * Dexie preferences table under the key `dashboardName:${id}`.
   */
  dashboardName?: string;
};

type DatasetState = {
  /** All loaded datasets, keyed by dataset id. */
  datasets: Map<string, DatasetEntry>;
  /** Currently active dataset id. */
  activeDatasetId: string | null;

  // --- Legacy single-dataset API (Phase 1 compat) ---
  status: DatasetStatus;

  /** Register a loaded dataset and make it active. */
  addDataset: (preview: DatasetPreview, fileName: string) => void;
  /** Switch the active dataset. */
  setActiveDataset: (id: string) => void;
  /** Remove a dataset from the registry. */
  removeDataset: (id: string) => void;
  /**
   * Wipe every dataset and lifecycle state from the in-memory store.
   * Filters are reset through {@link useFiltersStore} alongside.
   * Disk-side cleanup (Dexie tables, DuckDB tables in OPFS) is the
   * caller's responsibility — see `clearAllStorage` in `lib/clear-all`.
   */
  clearAll: () => void;
  /** Attach (or update) a column profile on an existing dataset. */
  setDatasetProfile: (id: string, profile: ColumnProfile[]) => void;
  /**
   * Replace dataset metadata after a typed-view rebuild. Updates
   * columns, preview, and overrides in one pass; clears stale profile
   * and any per-column value groupings (a synonym map built on
   * VARCHAR can't survive a re-type to DATE / DOUBLE). Filters are
   * cleared via {@link useFiltersStore} because previously-valid
   * chips could now reference a re-typed column.
   */
  applyOverrides: (
    id: string,
    preview: DatasetPreview,
    overrides: ColumnOverrides,
  ) => void;
  /**
   * Replace the per-column value groupings (synonym maps + numeric
   * bins). Because transforms are applied query-time, no view rebuild
   * is needed — we just update the store and invalidate any cached
   * query results upstream. Pass an empty object to clear all
   * groupings.
   */
  setValueGroupings: (
    id: string,
    groupings: Record<string, ColumnTransform>,
  ) => void;
  /** Update lifecycle status (loading/error). */
  setStatus: (next: DatasetStatus) => void;
  /**
   * Update the user-facing dashboard name for a dataset. Empty /
   * whitespace-only strings reset the entry's name (callers fall
   * back to the file basename). Persistence is fire-and-forget
   * through the preferences table so a Dexie hiccup never blocks
   * the UI.
   */
  setDashboardName: (id: string, name: string) => void;
};

/**
 * Reset the filters store via its `reset` action — kept as a tiny
 * helper so every dataset-store lifecycle hook ends with a single
 * intention-revealing call instead of inlining the cross-store write.
 */
function resetFilters(): void {
  useFiltersStore.getState().reset();
}

export const useDatasetStore = create<DatasetState>((set) => ({
  datasets: new Map(),
  activeDatasetId: null,
  status: { kind: 'idle' },

  addDataset: (preview, fileName) => {
    set((state) => {
      const entry: DatasetEntry = {
        id: preview.datasetId,
        fileName,
        rowCount: preview.rowCount,
        columns: preview.columns,
        preview,
      };
      const next = new Map(state.datasets);
      next.set(entry.id, entry);
      return {
        datasets: next,
        activeDatasetId: entry.id,
        status: { kind: 'ready', preview },
      };
    });
    resetFilters();
  },

  setActiveDataset: (id) => {
    set((state) => {
      const entry = state.datasets.get(id);
      if (!entry) return state;
      return {
        activeDatasetId: id,
        status: { kind: 'ready', preview: entry.preview },
      };
    });
    resetFilters();
  },

  removeDataset: (id) => {
    let switched = false;
    set((state) => {
      const next = new Map(state.datasets);
      next.delete(id);
      const newActive = state.activeDatasetId === id
        ? (next.keys().next().value as string | undefined) ?? null
        : state.activeDatasetId;
      switched = state.activeDatasetId === id;
      const newEntry = newActive ? next.get(newActive) : undefined;
      return {
        datasets: next,
        activeDatasetId: newActive,
        status: newEntry
          ? { kind: 'ready', preview: newEntry.preview }
          : { kind: 'idle' },
      };
    });
    if (switched) resetFilters();
  },

  clearAll: () => {
    set({
      datasets: new Map(),
      activeDatasetId: null,
      status: { kind: 'idle' },
    });
    resetFilters();
  },

  setDatasetProfile: (id, profile) =>
    set((state) => {
      const existing = state.datasets.get(id);
      if (!existing) return state;
      const next = new Map(state.datasets);
      next.set(id, { ...existing, profile });
      return { datasets: next };
    }),

  applyOverrides: (id, preview, overrides) => {
    let didUpdate = false;
    set((state) => {
      const existing = state.datasets.get(id);
      if (!existing) return state;
      const next = new Map(state.datasets);
      const hasOverrides = Object.keys(overrides).length > 0;

      // Spread `existing` so unrelated fields (notably `dashboardName`)
      // survive the rebuild — the previous literal-only construction
      // accidentally dropped any field outside its hard-coded list.
      // Then explicitly drop the fields whose values no longer make
      // sense after a column re-type:
      //   - `profile`: stale; the UI re-requests it from the worker.
      //   - `valueGroupings`: a synonym map built on VARCHAR can't be
      //     reused on a DATE / DOUBLE column. Keys would silently
      //     no-op against the new type.
      const updated: DatasetEntry = {
        ...existing,
        columns: preview.columns,
        rowCount: preview.rowCount,
        preview,
      };
      if (hasOverrides) updated.overrides = overrides;
      else delete updated.overrides;
      delete updated.profile;
      delete updated.valueGroupings;

      next.set(id, updated);
      didUpdate = true;
      return {
        datasets: next,
        status: { kind: 'ready', preview },
      };
    });
    // Column types may have changed — previously-valid drill-down
    // chips could now reference a re-typed column. Clear filters to
    // stay safe.
    if (didUpdate) resetFilters();
  },

  setValueGroupings: (id, groupings) =>
    set((state) => {
      const existing = state.datasets.get(id);
      if (!existing) return state;
      const next = new Map(state.datasets);
      const hasGroupings = Object.keys(groupings).length > 0;
      const {
        valueGroupings: _prevGroupings,
        ...rest
      } = existing;
      void _prevGroupings;
      const updated: DatasetEntry = hasGroupings
        ? { ...rest, valueGroupings: groupings }
        : rest;
      next.set(id, updated);
      return { datasets: next };
    }),

  setStatus: (next) => set({ status: next }),

  setDashboardName: (id, name) =>
    set((state) => {
      const existing = state.datasets.get(id);
      if (!existing) return state;
      const trimmed = name.trim();
      const next = new Map(state.datasets);
      const { dashboardName: _drop, ...rest } = existing;
      void _drop;
      const updated: DatasetEntry =
        trimmed.length > 0
          ? { ...rest, dashboardName: trimmed }
          : rest;
      next.set(id, updated);
      // Fire-and-forget persistence — never block the UI on Dexie.
      const key = `${DASHBOARD_NAME_KEY_PREFIX}${id}`;
      void configDb.preferences.put({ key, value: trimmed });
      return { datasets: next };
    }),
}));
